/**
 * channels.js — Unified Channels API
 * Replaces all Supabase calls with real backend endpoints.
 *
 * Base URL: https://ac-backend-stagging.up.railway.app
 * All endpoints under /bot/{channel}/...
 */

import { getAuthHeaders } from './auth';
import { getBaseUrl } from './utils';

// ── Config constants (same as before — used by JSX components) ──────────
export const FB_APP_ID = window.__FB_APP_ID__ || '';
export const IG_APP_ID = window.__IG_APP_ID__ || '';
export const WA_APP_ID = window.__WA_APP_ID__ || '';
export const WA_CONFIG_ID = window.__WA_CONFIG_ID__ || '';
export const FB_API_VERSION = 'v18.0';
export const FB_SCOPES = 'pages_show_list,pages_messaging,pages_read_engagement,pages_manage_metadata';
export const IG_SCOPES = 'instagram_basic,instagram_manage_messages,pages_show_list';
export const GOOGLE_CLIENT_ID = window.__GOOGLE_CLIENT_ID__ || '';
export const GOOGLE_REDIRECT_URI = `${getBaseUrl()}/bot/email/oauth/callback`;
export const GOOGLE_SCOPES = 'openid email profile https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/gmail.send';

// Kept for backward compat — embed code now uses /bot/script/{id}/update-config
export const WIDGET_SCRIPT_URL = `${getBaseUrl()}/bot/script`;
export const WIDGET_BASE_URL = `${getBaseUrl()}/bot/script`;

// Legacy URL exports (kept for backward compat with existing JSX)
export const WEBCHAT_API_URL = `${getBaseUrl()}/bot/webchat`;
export const WHATSAPP_ACCESS_TOKEN_URL = `${getBaseUrl()}/bot/whatsapp/oauth/token`;
export const WABA_TOKEN_PATH = `${getBaseUrl()}/bot/whatsapp/waba-token`;
export const FB_PAGE_ACCESS_TOKEN_URL = `${getBaseUrl()}/bot/facebook/page-token`;
export const IG_PAGE_ACCESS_TOKEN_URL = `${getBaseUrl()}/bot/instagram/page-token`;
export const FB_REPLY_SETTINGS_STATUS_URL = `${getBaseUrl()}/bot/facebook/reply-settings`;
export const FB_REPLY_SETTINGS_UPDATE_URL = `${getBaseUrl()}/bot/facebook/reply-settings`;
export const FB_DISCONNECT_URL = `${getBaseUrl()}/bot/facebook/disconnect`;
export const FB_RECONNECT_URL = `${getBaseUrl()}/bot/facebook/reconnect`;
export const IG_DISCONNECT_URL = `${getBaseUrl()}/bot/instagram/disconnect`;
export const IG_RECONNECT_URL = `${getBaseUrl()}/bot/instagram/reconnect`;
export const WORDPRESS_API_URL = `${getBaseUrl()}/bot/wordpress/connect`;
export const WORDPRESS_CONNECT_FALLBACK = WORDPRESS_API_URL;
export const WORDPRESS_DISCONNECT_URL = `${getBaseUrl()}/bot/wordpress/disconnect`;

// ── Helpers ─────────────────────────────────────────────────────────────

async function parseJson(res) {
  const data = await res.json();
  if (!res.ok) {
    const message = data?.message || data?.error || res.statusText || 'Request failed';
    throw new Error(message);
  }
  if (data.status === 'success' && data.data) return data.data;
  return data.data != null ? data.data : data;
}

async function apiGet(path) {
  const res = await fetch(`${getBaseUrl()}${path}`, {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return parseJson(res);
}

async function apiPost(path, body) {
  const res = await fetch(`${getBaseUrl()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(body),
  });
  return parseJson(res);
}

async function apiPut(path, body) {
  const res = await fetch(`${getBaseUrl()}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(body),
  });
  return parseJson(res);
}

async function apiDelete(path) {
  const res = await fetch(`${getBaseUrl()}${path}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return parseJson(res);
}

// ── Legacy helpers (kept for backward compat) ───────────────────────────

export function getCompanyId() {
  try {
    const user = JSON.parse(localStorage.getItem('aerochat_user') || '{}');
    return user.company_id || null;
  } catch { return null; }
}

export function getUserSecretKey() {
  try {
    const user = JSON.parse(localStorage.getItem('aerochat_user') || '{}');
    return user.secretkey || user.secret_key || '';
  } catch { return ''; }
}

export function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : '';
}

// ═══════════════════════════════════════════════════════════════════════════
// CHANNEL SUMMARY (master table)
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchChannelSummary() {
  return apiGet('/bot/channels/summary');
}

// Live per-channel-table check used by the Standard onboarding "Verify Now".
// Returns { active, webchat, whatsapp, instagram, facebook, email, telegram }.
export async function fetchActiveChannelStatus() {
  return apiGet('/bot/channels/active-status');
}

// ═══════════════════════════════════════════════════════════════════════════
// WEBCHAT
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchWebchatFromDb() {
  try {
    const data = await apiGet('/bot/webchat/account');
    if (data && data.widget_id) {
      return { exists: true, row: { widget_id: data.widget_id, webpage: data.domain, embedded_instruction: data.embed_code } };
    }
    return { exists: false };
  } catch { return { exists: false }; }
}

export async function saveWebchatDomain(domain) {
  try {
    const data = await apiPost('/bot/webchat/save-domain', { domain });
    return { ok: true, embedCode: data.embed_code, widgetId: data.widget_id };
  } catch (err) { return { ok: false, message: err.message }; }
}

export async function upsertWebchatRow(widgetId, webpage) {
  // Backend handles this in save-domain; this is a compat wrapper
  return saveWebchatDomain(webpage || '');
}

export function generateWidgetId(domain) {
  // Deterministic hash — kept for backward compat but backend now handles this
  let hash = 0;
  const str = `aerochat-${domain || 'default'}`;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(16).padStart(32, '0').slice(0, 32);
}

export function copyWebchatEmbedCode(embedCode, domain) {
  navigator.clipboard.writeText(embedCode || '').catch(console.error);
}

// ═══════════════════════════════════════════════════════════════════════════
// WHATSAPP
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchWhatsappAccountsFromDb() {
  try {
    const data = await apiGet('/bot/whatsapp/accounts');
    return { exists: Array.isArray(data) && data.length > 0, rows: data || [] };
  } catch { return { exists: false, rows: [] }; }
}

export async function insertWhatsappRow(accountName, phoneNumber) {
  return apiPost('/bot/whatsapp/connect', { account_name: accountName, phone_number: phoneNumber });
}

export async function deleteWhatsappRow(accountId) {
  return apiDelete(`/bot/whatsapp/${accountId}`);
}

export async function fetchWaSettingsUsername(accountId) {
  try {
    const data = await apiGet('/bot/whatsapp/accounts');
    const account = (data || []).find(a => a.id === accountId);
    return { ok: true, username: account?.settings_username || account?.name || '' };
  } catch { return { ok: false, username: '' }; }
}

export async function updateWaSettingsUsername(accountId, username) {
  return apiPut(`/bot/whatsapp/${accountId}/settings`, { settings_username: username });
}

// ═══════════════════════════════════════════════════════════════════════════
// FACEBOOK
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchFacebookAccountsFromDb() {
  try {
    const data = await apiGet('/bot/facebook/accounts');
    return { exists: Array.isArray(data) && data.length > 0, rows: data || [] };
  } catch { return { exists: false, rows: [] }; }
}

export async function insertFacebookRow(pageName, pageId, pageIdentifier, pageAccessToken) {
  return apiPost('/bot/facebook/connect', {
    page_name: pageName, page_id: pageId, page_identifier: pageIdentifier, page_access_token: pageAccessToken,
  });
}

export async function deleteFacebookRow(accountId) {
  return apiDelete(`/bot/facebook/${accountId}`);
}

// ═══════════════════════════════════════════════════════════════════════════
// INSTAGRAM
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchInstagramAccountsFromDb() {
  try {
    const data = await apiGet('/bot/instagram/accounts');
    return { exists: Array.isArray(data) && data.length > 0, rows: data || [] };
  } catch { return { exists: false, rows: [] }; }
}

export async function insertInstagramRow(username, igUserId, pageAccessToken) {
  return apiPost('/bot/instagram/connect', {
    username, ig_user_id: igUserId, page_access_token: pageAccessToken,
  });
}

export async function deleteInstagramRow(accountId) {
  return apiDelete(`/bot/instagram/${accountId}`);
}

// ═══════════════════════════════════════════════════════════════════════════
// TELEGRAM
// ═══════════════════════════════════════════════════════════════════════════

// Fetches the current Telegram bot info from the backend.
export async function fetchTelegramInfo() {
  try {
    const base = getBaseUrl();
    const url = `${base}/bot/telegram/info`.replace(/([^:]\/)\/+/g, '$1');

    const res = await fetch(url, {
      method: 'GET',
      headers: {
        ...getAuthHeaders(),
      },
    });

    if (!res.ok) return { connected: false };

    const data = await res.json();
    if (data.telegram) {
      return {
        connected: true,
        bot_username: data.telegram.bot_username ? `@${data.telegram.bot_username}` : '',
        created_at: data.telegram.created_at || null,
      };
    }
    return { connected: false };
  } catch {
    return { connected: false };
  }
}

// Submits a Telegram bot token via the backend register API.
export async function submitTelegramBotToken(token) {
  try {
    const base = getBaseUrl();
    const url = `${base}/bot/telegram/register`.replace(/([^:]\/)\/+/g, '$1');

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ bot_token: token }),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      const msg = data.detail || data.message || 'Failed to register bot.';
      if (res.status === 409) {
        return { ok: false, message: 'This bot token is already registered.' };
      }
      if (res.status === 400) {
        return { ok: false, message: 'Invalid bot token. Please check with @BotFather and try again.' };
      }
      return { ok: false, message: msg };
    }

    const botUsername = data.bot_username ? `@${data.bot_username}` : 'your bot';

    return {
      ok: true,
      message: `Bot ${botUsername} connected successfully!`,
      bot_username: botUsername,
    };
  } catch (err) {
    const msg = err.message || 'Something went wrong.';
    if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
      return { ok: false, message: 'Network error. Please check your connection and try again.' };
    }
    return { ok: false, message: msg };
  }
}

export async function checkTelegramInDb() {
  try {
    const info = await fetchTelegramInfo();
    if (info.connected) {
      return { exists: true, row: { bot_token: true, username: info.bot_username } };
    }
    return { exists: false, row: null };
  } catch { return { exists: false, row: null }; }
}

// Reconnect: refreshes the Telegram webhook via the backend API.
export async function reconnectTelegram() {
  try {
    const base = getBaseUrl();
    const url = `${base}/bot/telegram/reconnect`.replace(/([^:]\/)\/+/g, '$1');

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
      },
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      return { ok: false, message: data.detail || 'Failed to reconnect.' };
    }

    return {
      ok: true,
      bot_username: data.bot_username ? `@${data.bot_username}` : '',
    };
  } catch {
    return { ok: false, message: 'Network error.' };
  }
}

// Disconnect: removes the Telegram bot via the backend API.
export async function disconnectTelegram() {
  try {
    const base = getBaseUrl();
    const url = `${base}/bot/telegram/disconnect`.replace(/([^:]\/)\/+/g, '$1');

    const res = await fetch(url, {
      method: 'DELETE',
      headers: {
        ...getAuthHeaders(),
      },
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      return { ok: false, message: data.detail || 'Failed to disconnect.' };
    }

    return { ok: true };
  } catch {
    return { ok: false, message: 'Network error.' };
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// EMAIL
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchEmailAccountsFromDb() {
  try {
    const data = await apiGet('/bot/email/accounts');
    return { exists: Array.isArray(data) && data.length > 0, rows: data || [] };
  } catch { return { exists: false, rows: [] }; }
}

export async function insertEmailRow(email, name, platform, avatar, accessToken, refreshToken) {
  return apiPost('/bot/email/connect', {
    email_address: email, account_name: name, platform: platform || 'Gmail',
    avatar, access_token: accessToken, refresh_token: refreshToken,
  });
}

export async function deleteEmailRow(accountId) {
  return apiDelete(`/bot/email/${accountId}`);
}

// ═══════════════════════════════════════════════════════════════════════════
// WORDPRESS
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchWordpressFromDb() {
  try {
    const data = await apiGet('/bot/wordpress/account');
    if (data && data.site_url) {
      return { exists: true, wordpress_url: data.site_url };
    }
    return { exists: false, wordpress_url: '' };
  } catch { return { exists: false, wordpress_url: '' }; }
}

export async function insertWordpressRow(siteUrl) {
  return apiPost('/bot/wordpress/connect', { site_url: siteUrl });
}

export async function deleteWordpressRow() {
  return apiDelete('/bot/wordpress/disconnect');
}

// ── Return to the Shopify embedded app after a redirectTop()-driven connect ──
// `url` is null when this merchant isn't a Shopify merchant, so callers just
// stay put in that case.
export async function fetchEmbedReturnUrl(channel) {
  try {
    const data = await apiGet(`/bot/channels/embed-return-url?channel=${encodeURIComponent(channel)}`);
    return data && data.url ? data.url : null;
  } catch { return null; }
}
